import Big from 'big.js'
import clsx from 'clsx'
import {countries, type TCountryCode} from 'countries-list'

import RedoIcon from '@/assets/icons/redo.svg?react'
import CopyableText from '@/components/CopyableText'
import Loading from '@/components/Loading/Loading'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import useOrder from '@/lib/order/hooks/useOrder'
import {OrderItemStatus, OrderStatus} from '@/models/orderModel'
import {OrderDetailsRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {formatLocaleDateTime} from '@/utils/datetime'
import NotFound from '@/views/NotFound/NotFound'

import BuyAgainDialog from './components/BuyAgainDialog'
import CancelDialog from './components/CancelDialog'
import ReturnDialog from './components/ReturnDialog'
import style from './OrderDetails.module.scss'

const ORDER_ITEM_STATUS_LABELS: Record<OrderItemStatus, string> = {
  [OrderItemStatus.SHIPPED]: 'Shipped',
  [OrderItemStatus.OUT_FOR_DELIVERY]: 'Out for delivery',
  [OrderItemStatus.DELIVERED]: 'Delivered',
  [OrderItemStatus.RETURN_REQUESTED]: 'Return requested',
  [OrderItemStatus.RETURNED]: 'Returned',
  [OrderItemStatus.REFUNDED]: 'Refunded',
  [OrderItemStatus.COMPLETED]: 'Completed',
  [OrderItemStatus.CONFIRMED]: 'Confirmed',
}

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  [OrderStatus.CONFIRMED]: 'Confirmed',
  [OrderStatus.CANCELLATION_REQUESTED]: 'Cancellation requested',
  [OrderStatus.SHIPPED]: 'Shipped',
  [OrderStatus.OUT_FOR_DELIVERY]: 'Out for delivery',
  [OrderStatus.DELIVERED]: 'Delivered',
  [OrderStatus.COMPLETED]: 'Completed',
  [OrderStatus.CANCELLED]: 'Cancelled',
  [OrderStatus.RETURN_REQUESTED]: 'Return requested',
  [OrderStatus.RETURNED]: 'Returned',
  [OrderStatus.REFUNDED]: 'Refunded',
  [OrderStatus.FAILED]: 'Failed',
}

function getItemStatusLabel(status: OrderItemStatus | undefined): string {
  return status ? ORDER_ITEM_STATUS_LABELS[status] : 'Confirmed'
}

function hasShippingInfo(item: {carrier?: string; shippingTrackingNumber?: string}): boolean {
  return Boolean(item.carrier ?? item.shippingTrackingNumber)
}

export default memo(function OrderDetails() {
  const {orderId} = OrderDetailsRoute.useParams()
  const [locale] = useLocale()
  const {data: order, isLoading} = useOrder(orderId, locale)
  const [isAnyDialogOpen, setIsAnyDialogOpen] = useState(false)
  console.log(order)

  // eslint-disable-next-line @eslint-react/hooks-extra/no-unnecessary-use-callback -- it's necessary
  const goBack = useCallback(() => {
    history.back()
  }, [])

  if (isLoading) return <Loading label='Loading order details...' />
  if (!order) return <NotFound />

  const subtotal = order.lineItems.reduce(
    (acc, item) => acc.add(Big(item.price).times(item.quantity)),
    Big(0),
  )
  const discountValue = Big(order.moreInfo?.amountDiscount ?? 0).times(-1)
  const deliveryFee = Big(order.moreInfo?.deliveryFee ?? 0)
  const totalBeforeTax = subtotal.add(deliveryFee).add(discountValue)
  const tax = Big(order.paymentInfo.amountTax ?? 0)
  const roundUp = Big(order.moreInfo?.amountRoundUp ?? 0)
  const total = totalBeforeTax.plus(tax).plus(roundUp)

  const isReturnable = order.lineItems.some(item => item.status === OrderItemStatus.DELIVERED)
  const isCancelable = order.lineItems.some(item => item.status === OrderItemStatus.CONFIRMED)

  return (
    <>
      <HeadTags title={`Order #${order.id}`} />
      <PrimaryButton className={style.GoBack} onPress={goBack}>
        Back
      </PrimaryButton>
      <main className={clsx(style.OrderDetailsSection, isAnyDialogOpen && style.WithBackdrop)}>
        <header className={style.HeaderRow}>
          <h1
            className={style.Heading}
            aria-label={`Order ${order.id}, total: $${total.toFixed(USD_DISPLAY_DECIMALS)}`}
          >
            Order details
          </h1>
          <div
            className={clsx(
              style.StatusBadge,
              (order.status === OrderStatus.COMPLETED || order.status === OrderStatus.DELIVERED) &&
                style.GreenStatus,
            )}
            role='status'
            aria-label={`Order status: ${order.status ? ORDER_STATUS_LABELS[order.status] : 'Confirmed'}`}
          >
            {order.status ? ORDER_STATUS_LABELS[order.status] : 'Confirmed'}
          </div>
        </header>
        <div className={style.OrderSummary}>
          <span>
            {order.lineItems.length} {order.lineItems.length === 1 ? 'item' : 'items'} ordered on{' '}
            {formatLocaleDateTime(order.createdAt, 'en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
          <span>Order # {order.id}</span>
        </div>

        <div className={style.TwoColumnLayout}>
          {order.shippingInfo && (
            <section className={style.ShippingSection} aria-labelledby='shipping-heading'>
              <h3 className={style.DeliverTo} id='shipping-heading'>
                Deliver to: <strong>{order.shippingInfo.name}</strong>
              </h3>
              <div className={style.ShippingAddress}>
                <div className={style.AddressLine}>
                  <VisuallyHidden>Address line 1: </VisuallyHidden>
                  {order.shippingInfo.line1}
                </div>
                {order.shippingInfo.line2 && (
                  <div className={style.AddressLine}>
                    <VisuallyHidden>Address line 2: </VisuallyHidden>
                    {order.shippingInfo.line2}
                  </div>
                )}
                <div className={style.AddressLine}>
                  <VisuallyHidden>City: </VisuallyHidden>
                  {order.shippingInfo.city}, <VisuallyHidden>State: </VisuallyHidden>
                  {order.shippingInfo.state} <VisuallyHidden>Postal code: </VisuallyHidden>
                  {order.shippingInfo.postalCode}
                </div>
                <div className={style.AddressLine}>
                  <VisuallyHidden>Country: </VisuallyHidden>
                  {countries[order.shippingInfo.country as unknown as TCountryCode].name}
                </div>
              </div>
            </section>
          )}

          <section className={style.DeliveryInfo} aria-labelledby='order-summary-heading'>
            <h3 className={style.OrderSummaryTitle} id='order-summary-heading'>
              Order summary
            </h3>
            <div className={style.DeliveryItems}>
              <div
                className={style.DeliveryItem}
                aria-label={`Subtotal for ${order.lineItems.length} ${order.lineItems.length === 1 ? 'item' : 'items'}: ${subtotal} dollars`}
              >
                <div className={style.DeliveryItemName} aria-hidden='true'>
                  Subtotal ({order.lineItems.length}{' '}
                  {order.lineItems.length === 1 ? 'item' : 'items'})
                  <VisuallyHidden>:</VisuallyHidden>
                </div>
                <div className={style.DeliveryItemPrice} aria-hidden='true'>
                  <Price
                    price={subtotal}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </div>
              </div>
              <div
                className={style.DeliveryItem}
                aria-label={`Delivery fee: ${deliveryFee} dollars`}
              >
                <div className={style.DeliveryItemName} aria-hidden='true'>
                  Delivery Fee<VisuallyHidden>:</VisuallyHidden>
                </div>
                <div className={style.DeliveryItemPrice} aria-hidden='true'>
                  <Price
                    price={deliveryFee}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </div>
              </div>
              <div className={style.DeliveryItem} aria-label={`Tax: ${tax} dollars`}>
                <div className={style.DeliveryItemName} aria-hidden='true'>
                  Tax<VisuallyHidden>:</VisuallyHidden>
                </div>
                <div className={style.DeliveryItemPrice} aria-hidden='true'>
                  <Price
                    price={tax}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </div>
              </div>
              {roundUp.gt(0) && (
                <div
                  className={style.DeliveryItem}
                  aria-label={`Donation to NFB: ${roundUp} dollars`}
                >
                  <div className={style.DeliveryItemName} aria-hidden='true'>
                    Donate change to NFB<VisuallyHidden>:</VisuallyHidden>
                  </div>
                  <div className={style.DeliveryItemPrice} aria-hidden='true'>
                    <Price
                      price={roundUp}
                      options={{
                        fractionDigits: USD_DISPLAY_DECIMALS,
                        exactFractionDigits: true,
                      }}
                    />
                  </div>
                </div>
              )}
              <div
                className={style.DeliveryItem}
                aria-label={`Total discount: ${discountValue} dollars`}
              >
                <div className={style.DeliveryItemName} aria-hidden='true'>
                  Discount (Total)<VisuallyHidden>:</VisuallyHidden>
                </div>
                <div
                  className={clsx(style.DeliveryItemPrice, discountValue.lt(0) && style.GoodValue)}
                  aria-hidden='true'
                >
                  <Price
                    price={discountValue}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={style.Total} aria-label={`Order total: ${total} dollars`}>
              <span aria-hidden='true'>
                Total<VisuallyHidden>:</VisuallyHidden>
              </span>
              <span aria-hidden='true'>
                <Price
                  price={total}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </span>
            </div>
          </section>
        </div>

        <section className={style.OrderItemsSection} aria-labelledby='order-items-heading'>
          <VisuallyHidden as='h2' id='order-items-heading'>
            Order items
          </VisuallyHidden>
          <div className={style.OrderItems}>
            {order.lineItems.map(item => (
              <div key={`${item.retailer}-${item.id}`}>
                <article
                  className={style.OrderItemWrapper}
                  aria-labelledby={`item-${item.retailer}-${item.id}`}
                >
                  <div className={style.OrderItem}>
                    <div
                      className={style.ItemQuantity}
                      data-quantity={item.quantity}
                      aria-label={`Quantity: ${item.quantity}`}
                    >
                      <VisuallyHidden>&nbsp;</VisuallyHidden>
                      <span className={style.QuantityDesktop} aria-hidden='true'>
                        {item.quantity}x
                      </span>
                      <span className={style.QuantityMobile} aria-hidden='true'>
                        x {item.quantity}
                      </span>
                    </div>

                    <div className={style.ItemImage}>
                      <img src={item.image} alt='' aria-hidden='true' loading='lazy' />
                    </div>

                    <div className={style.ItemDetails}>
                      <h3 className={style.ItemTitle} id={`item-${item.retailer}-${item.id}`}>
                        <strong>{item.title}</strong>
                      </h3>

                      {item.variation && (
                        <div className={style.ItemVariation}>
                          {Object.entries(item.variation)
                            .map(
                              ([key, value]) =>
                                `${key.slice(0, 1).toUpperCase() + key.slice(1)}: ${value}`,
                            )
                            .join(' | ')}
                        </div>
                      )}

                      <div className={style.ItemSoldBy}>
                        Sold by: {item.seller ?? item.retailer}
                      </div>

                      <div className={style.ItemShippingInfo}>
                        <div className={style.ShippingInfoLine}>
                          <span
                            className={style.ItemStatus}
                            aria-label={`Status: ${getItemStatusLabel(item.status)}`}
                          >
                            <VisuallyHidden>&nbsp;</VisuallyHidden>
                            <span aria-hidden='true'>{getItemStatusLabel(item.status)},</span>
                          </span>
                          {item.estimatedDeliveryDate && (
                            <span
                              className={style.ItemDeliveryDate}
                              aria-label={`Estimated delivery: ${item.estimatedDeliveryDate}`}
                            >
                              <VisuallyHidden>&nbsp;</VisuallyHidden>
                              <span aria-hidden='true'>{item.estimatedDeliveryDate}</span>
                            </span>
                          )}
                        </div>
                        {hasShippingInfo(item) && (
                          <div className={style.ShippingInfoLine}>
                            {item.carrier && (
                              <span className={style.ItemCarrier}>{item.carrier}</span>
                            )}
                            {item.shippingTrackingNumber && (
                              <span className={style.ItemTrackingNumber}>
                                <CopyableText
                                  text={item.shippingTrackingNumber}
                                  label='tracking number'
                                >
                                  <a
                                    href={`https://www.aftership.com/track/${item.shippingTrackingNumber}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                  >
                                    #{item.shippingTrackingNumber}
                                  </a>
                                </CopyableText>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={style.PriceContainer}>
                    <div className={style.ItemPrice}>
                      <strong>
                        <Price
                          price={Big(item.price)}
                          options={{
                            fractionDigits: USD_DISPLAY_DECIMALS,
                            exactFractionDigits: true,
                          }}
                        />
                      </strong>
                    </div>
                    <div className={style.BuyAgainContainer}>
                      <BuyAgainDialog
                        order={order}
                        preSelectedItemKey={`${item.retailer}-${item.id}`}
                        onOpenChange={setIsAnyDialogOpen}
                      >
                        <PrimaryButton
                          size='sm'
                          className={style.BuyAgainButton}
                          aria-label={`Buy ${item.title} again`}
                        >
                          Buy Again
                        </PrimaryButton>
                      </BuyAgainDialog>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </section>

        <div className={style.Actions}>
          {isReturnable && (
            <ReturnDialog order={order} onOpenChange={setIsAnyDialogOpen}>
              <PrimaryButton
                size='lg'
                className={style.ReturnButton}
                aria-label={`Return items from Order #${order.id}`}
              >
                <RedoIcon />
                <span className={style.ReturnText}>Return</span>
              </PrimaryButton>
            </ReturnDialog>
          )}
          {isCancelable && (
            <CancelDialog orderId={order.id} onOpenChange={setIsAnyDialogOpen}>
              <PrimaryButton
                size='lg'
                className={style.CancelButton}
                aria-label={`Request to cancel Order #${order.id}`}
              >
                Request to cancel
              </PrimaryButton>
            </CancelDialog>
          )}
        </div>
      </main>
    </>
  )
})
